// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { Tooltip, Toastr } from "@revvsales/components";
import moment from "moment";

// Components Imports
import DashboardLoader from "../DashboardLoader";
import { TableChart } from "../Charts";

// Actions Imports
import DashboardActions from "../../../actions/DashboardActions";

// Internal Imports
import { DASHBOARD } from "../../../constants";
import { GlobalUtils, OppoUtils } from "../../../utils";

// Destructuring
const {
  getDashboardHighValueDeals,
  getDashboardAccounts,
  getDashboardLeaderboard,
} = DashboardActions;
const { handleApiErrMsg, numberForamting } = GlobalUtils;
const { decideDeadlineDateClassName, decideDeadlineDaysCount } = OppoUtils;
const {
  ACCOUNTS_TABLE_CHART,
  HIGH_VALUE_DEALS_TABLE_CHART,
  LEADERBOARD_TABLE_CHART,
  TABLE_CHART_CONFIGS,
} = DASHBOARD;

class TableChartWrapper extends Component {
  state = { showLoader: false };

  componentDidMount() {
    this._onInitData();
  }

  _toggleLoader = flag => {
    this.setState({ showLoader: flag });
  };

  _onInitData = () => {
    this._toggleLoader(true);
    const { currentView } = this.props;
    this._decideActionName(currentView)()
      .then(() => {
        this._toggleLoader(false);
      })
      .catch(error => {
        console.error(`${currentView}(index.js) => _onInitData => ${error}`);
        this._toggleLoader(false);
        Toastr.showToastr({
          type: "failure",
          message: handleApiErrMsg(error),
          timeout: 3000,
        });
      });
  };

  _decideActionName = currentView => {
    const { apiConfigs: { userID, fromDate, toDate } = {} } = this.props;
    switch (currentView) {
      case HIGH_VALUE_DEALS_TABLE_CHART:
        return () =>
          this.props.getDashboardHighValueDeals(userID, fromDate, toDate);
      case LEADERBOARD_TABLE_CHART:
        return () => this.props.getDashboardLeaderboard(fromDate, toDate);
      case ACCOUNTS_TABLE_CHART:
        return () => this.props.getDashboardAccounts(userID, fromDate, toDate);
      default:
        return null;
    }
  };

  _getApproversProfiles = users => {
    let requiredUsers = users;
    if (users && users.length > 3) {
      requiredUsers = users.slice(0, 3);
    }
    return requiredUsers.map(user => {
      if (user.is_active) {
        return (
          <span
            className="table-user-profile-pic overlap-profile-pic"
            key={user.id}>
            <Tooltip placement="top" overlay={user.name || "-"}>
              <img
                className="table-user-pic"
                src={user.image_url}
                alt="Approvers Profile"
              />
            </Tooltip>
          </span>
        );
      }
      return null;
    });
  };

  _getAccountsRows = rowsData =>
    rowsData.map(rowData => (
      <div className="tablechart-table-tr" key={rowData.account_name}>
        <span className="tablechart-table-td table-acc-name">
          {rowData.account_name}
        </span>
        <span className="tablechart-table-td right-align">
          {numberForamting(rowData.deal_value, "$")}
        </span>
        <span className="tablechart-table-td right-align">
          {numberForamting(rowData.pipeline_value, "$")}
        </span>
        <span className="tablechart-table-td center-align">
          {rowData.total_deal_value !== 0
            ? (
                (rowData.total_discount / rowData.total_deal_value) *
                100
              ).toFixed(2)
            : "0.00"}
          %
        </span>
        <span className="tablechart-table-td center-align">
          {rowData.opportunities_count}
        </span>
      </div>
    ));

  _getHighDealValuesRows = rowsData =>
    rowsData.map(rowData => {
      const dlDate = GlobalUtils.formatDate(rowData.turn_around_date, "lll");
      const currentDate = new Date();
      const deadlineLeftHours = moment(dlDate).diff(
        moment(currentDate),
        "hours",
      );
      return (
        <div className="tablechart-table-tr" key={rowData.acc_name}>
          <span className="tablechart-table-td table-acc-name">
            {rowData.acc_name}
          </span>
          <span className="tablechart-table-td">{rowData.opp_name}</span>
          <span className="tablechart-table-td right-align">
            {numberForamting(rowData.deal_size, "$")}
          </span>
          <span className="tablechart-table-td table-chart-imgwrapper center-align">
            <div className="tablerow-userprofile-wrapper">
              {rowData.users && this._getApproversProfiles(rowData.users)}
            </div>
            {/* :: ANKIT_TO_DO :: */}
            {/* {rowData.users.length > 2 ? (
              <span className="table-user-dotimage">...</span>
            ) : null} */}
          </span>
          <Tooltip
            placement="top"
            overlay={decideDeadlineDaysCount(deadlineLeftHours)}>
            <span
              className={`tablechart-table-td center-align ${decideDeadlineDateClassName(
                deadlineLeftHours,
              )}`}>
              {GlobalUtils.formatDate(rowData.turn_around_date, "ll")}
            </span>
          </Tooltip>
        </div>
      );
    });

  _getLeaderboardRows = rowsData =>
    rowsData.map(rowData => (
      <div
        className="tablechart-table-tr leaderboard-table-tr"
        key={rowData.user_id}>
        <span className="tablechart-table-td table-chart-imgwrapper no-heading-img">
          <span className="table-user-profile-pic">
            <img
              className="table-user-pic"
              src={rowData.users.image_url}
              alt="Doc Owner Profile"
            />
          </span>
        </span>
        <span className="tablechart-table-td table-chart-username">
          {rowData.users.name}
        </span>
        <span className="tablechart-table-td right-align leaderboard-dealtd">
          {numberForamting(rowData.deal_value, "$")}
        </span>
      </div>
    ));

  _decideRows = currentView => {
    const { highValueDeals, accounts, leaderboard } = this.props;
    switch (currentView) {
      case HIGH_VALUE_DEALS_TABLE_CHART:
        return this._getHighDealValuesRows(highValueDeals || []);
      case LEADERBOARD_TABLE_CHART:
        return this._getLeaderboardRows(leaderboard || []);
      case ACCOUNTS_TABLE_CHART:
        return this._getAccountsRows(accounts || []);
      default:
        return null;
    }
  };

  render() {
    if (this.state.showLoader) {
      return <DashboardLoader message="loading data" />;
    }

    const { showChartHeader, currentView } = this.props;
    const { headerTitle, tableHeadings } = TABLE_CHART_CONFIGS[currentView];
    let extraData;

    if (currentView === HIGH_VALUE_DEALS_TABLE_CHART) {
      const { highValueDeals } = this.props;
      let totalDealValue = 0;
      if (highValueDeals) {
        highValueDeals.forEach(deal => {
          totalDealValue += deal.deal_size;
        });
      }
      extraData = {
        title: "Total Value",
        value: `${numberForamting(totalDealValue, "$")}`,
      };
    }
    return (
      <div className="tablechart-container">
        <TableChart
          showHeader={showChartHeader}
          headerTitle={headerTitle}
          extraData={extraData}
          showTableHeading
          tableHeader={tableHeadings}
          tableRows={this._decideRows(currentView)}
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { highValueDeals, accounts, leaderboard } = state.DashboardStore;
  return { highValueDeals, accounts, leaderboard };
};

const mapDispatchToProps = {
  getDashboardHighValueDeals: (userID, fromDate, toDate) =>
    getDashboardHighValueDeals(userID, fromDate, toDate),
  getDashboardLeaderboard: (userID, fromDate, toDate) =>
    getDashboardLeaderboard(userID, fromDate, toDate),
  getDashboardAccounts: (userID, fromDate, toDate) =>
    getDashboardAccounts(userID, fromDate, toDate),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TableChartWrapper);



// WEBPACK FOOTER //
// ./src/components/DashboardWrapper/TableChartWrapper/index.js